import React, { useState, useMemo, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import MainButton from "../../../components/common/MainButton/MainButton";
import Heading from "../../../components/common/Typography/Heading";
import AuxiliaryText from "../../../components/common/Typography/AuxiliaryText";
import Paragraph from "../../../components/common/Typography/Paragraph";
import Snippet from "../../../components/common/SnippetIcon/Snippet";
import Spinner from "../../../components/common/Spinner";
import Alerts from "../../../components/common/Alerts/Alerts";
import StepBackButton from "../../../components/common/StepBackButton/StepBackButton";
import { UpdateUserMutation } from "../../../services/SignUp";
import { useGraphQL } from "../../../context/GraphQLProvider";
import { setField, setStep } from "../../../Redux/Slice/SignUp";
import { fetchServiceCategories } from "../../../Redux/Slice/serviceCategoriesSlice";
import { transformToUserInput } from "../../../utils/userInputUtils";
import { POPUP_FORM_CONTENT_CLASS } from "../../../components/popup/popupLayoutSpec";

const PrimarySkills = ({ onClickHandler }) => {
  const graphqlService = useGraphQL();
  const [RequestStart, setRequetStart] = useState(false);
  const dispatch = useDispatch();
  const InitialValues = useSelector((state) => state.signup);
  const { step, serviceCategoryId } = useSelector((state) => state.signup);
  const { categories, loading: categoriesLoading } = useSelector((state) => state.serviceCategories);

  // Load categories when we have a selected service but categories not yet loaded (e.g. user skipped from previous step)
  useEffect(() => {
    if (serviceCategoryId && categories.length === 0 && !categoriesLoading) {
      dispatch(fetchServiceCategories(graphqlService));
    }
  }, [serviceCategoryId, categories.length, categoriesLoading, dispatch, graphqlService]);

  // When skipping, dummy data sets serviceCategoryId "1" – resolve to Plumbing so subcategories show correctly
  useEffect(() => {
    if (categories.length === 0 || serviceCategoryId !== "1") return;
    const plumbingCategory = categories.find((c) => (c.name || "").trim().toLowerCase() === "plumbing");
    if (plumbingCategory) {
      dispatch(setField({ serviceCategoryId: plumbingCategory.id }));
    }
  }, [categories, serviceCategoryId, dispatch]);

  // Subcategories from the service category selected in the previous step (use == for string/number id match)
  const specialtyOptions = useMemo(() => {
    if (serviceCategoryId == null || serviceCategoryId === "" || !Array.isArray(categories) || categories.length === 0) {
      return [];
    }
    const selectedCategory = categories.find((c) => c.id == serviceCategoryId);
    const sub = selectedCategory?.subcategories;
    return Array.isArray(sub) && sub.length > 0 ? sub : [];
  }, [serviceCategoryId, categories]);

  const [selectedSkills, setSelectedSkills] = useState([]);
  const hasRestoredRef = useRef(false);

  // Restore saved specialities when subcategories load (e.g. returning to this step)
  useEffect(() => {
    hasRestoredRef.current = false;
  }, [serviceCategoryId]);

  useEffect(() => {
    if (specialtyOptions.length === 0 || hasRestoredRef.current) return;
    const saved = InitialValues.primarySkills;
    if (!Array.isArray(saved) || saved.length === 0) return;
    const validIds = new Set(specialtyOptions.map((s) => s.id));
    const restored = saved.filter((id) => validIds.has(id)).slice(0, 3);
    if (restored.length > 0) {
      setSelectedSkills(restored);
      hasRestoredRef.current = true;
    }
  }, [serviceCategoryId, specialtyOptions.length]);

  const handleSkillToggle = (skillId) => {
    setSelectedSkills((prev) => {
      if (prev.includes(skillId)) {
        return prev.filter((id) => id !== skillId);
      }
      if (prev.length >= 3) return prev;
      return [...prev, skillId];
    });
  };

  const handleSubmit = () => {
    if (specialtyOptions.length === 0) {
      Alerts.error("Please go back and select a trade category first");
      return;
    }
    if (selectedSkills.length === 0) {
      Alerts.error("Please select at least one featured service");
      return;
    }
    if (selectedSkills.length > 3) {
      Alerts.error("You can select up to 3 featured services");
      return;
    }

    setRequetStart(true);
    const userInput = transformToUserInput(InitialValues);
    userInput.userInput.primarySkills = selectedSkills;

    UpdateUserMutation(graphqlService, userInput)
      .then((response) => {
        if (response.updateUserProfile?.primarySkills) {
          dispatch(setField({ primarySkills: selectedSkills }));
          onClickHandler();
        }
      })
      .catch((error) => {
        Alerts.error(error);
      })
      .finally(() => {
        setRequetStart(false);
      });
  };

  const handleBack = () => {
    dispatch(setStep(step - 1));
  };

  return (
    <>
      {RequestStart && <Spinner />}
      <div className="overflow-auto overflow-visible relative flex items-center justify-center" style={{ minHeight: 'calc(100vh - 120px)' }}>
        <div className={POPUP_FORM_CONTENT_CLASS}>
          <div className="flex items-center justify-center mb-4">
            <Heading
              variant={"h6"}
              className={"text-center text-offblack mr-2"}
            >
              Featured services
            </Heading>
            <Snippet
              variant={"icon"}
              snippet={true}
              star={true}
              text={"Select up to 3 featured services to highlight on your profile. All other services from your trade category will be automatically included with your profile."}
              label={"Select featured services"}
            />
          </div>
          <div className="mt-10 xl:mt-[60px] w-full">
            <div className="flex justify-center w-full">
              <div className="w-full mx-auto">
                {specialtyOptions.length === 0 ? (
                  serviceCategoryId != null && serviceCategoryId !== "" ? (
                    <AuxiliaryText variant={"FieldLabel"} className="text-center text-mediumgray block py-6">
                      {categoriesLoading || categories.length === 0 ? "Loading featured services…" : "No featured services found for this trade."}
                    </AuxiliaryText>
                  ) : (
                    <AuxiliaryText variant={"FieldLabel"} className="text-center text-mediumgray block py-6">
                      Please go back and select a trade category first. Featured services are based on your selected trade.
                    </AuxiliaryText>
                  )
                ) : (
                  <div className="grid grid-cols-2 gap-x-10 gap-y-2">
                    {React.Children.toArray(
                      specialtyOptions.map((skill) => {
                        const isSelected = selectedSkills.includes(skill.id);
                        return (
                          <div key={skill.id} className="flex items-center mb-2 cursor-pointer" onClick={() => handleSkillToggle(skill.id)}>
                            <label
                              className="relative flex items-center rounded-full cursor-pointer mr-2"
                              htmlFor={`skill-${skill.id}`}
                            >
                              <input
                                type="checkbox"
                                id={`skill-${skill.id}`}
                                checked={isSelected}
                                onChange={() => handleSkillToggle(skill.id)}
                                onClick={(e) => e.stopPropagation()}
                                disabled={!isSelected && selectedSkills.length >= 3}
                                className="before:content[''] peer relative size-4 cursor-pointer appearance-none rounded-[4px] border border-mediumgray transition-all before:absolute before:top-2/4 before:left-2/4 before:block before:h-12 before:w-12 before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-blue-gray-500 before:opacity-0 before:transition-opacity checked:border-primaryblue checked:bg-primaryblue checked:before:bg-primaryblue disabled:opacity-50 disabled:cursor-not-allowed"
                              />
                              <span className="absolute text-white transition-opacity opacity-0 pointer-events-none top-[40%] left-2/4 -translate-y-2/4 -translate-x-2/4 peer-checked:opacity-100">
                                <i className="fi fi-rs-check text-[10px]"></i>
                              </span>
                            </label>
                            <Paragraph
                              variant={"MainParagraph"}
                              className="hover:text-primaryblue transition-colors"
                            >
                              {skill.name}
                            </Paragraph>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
                <div className="flex items-center justify-between mt-10 w-full">
                  <StepBackButton onClick={handleBack} />
                  <MainButton
                    variant={"small"}
                    onClick={handleSubmit}
                  >
                    Next
                  </MainButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrimarySkills; 